/* eslint-disable max-statements */
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import standardMetricsIcon from '../../assets/standardMetrics.png';
import additionalMetricsIcon from '../../assets/additionalMetrics.png';
import { ExportDailyMetric } from '../../components/exportDailyMetric/ExportDailyMetric';
import { MetricPayoutTable } from '../../components/metricPayoutTable/MetricPayoutTable';
import { MetricProgress } from '../../components/metricProgress/metricProgress';
import { MyDailyMetricsTable } from '../../components/myDailyMetricsTable/MyDailyMetricsTable';
import { MyMetricGreeting } from '../../components/myMetricGreeting/MyMetricGreeting';
import { MyMetricGreetingFilter } from '../../components/myMetricGreetingFilter/MyMetricGreetingFilter';
import { PayoutCalculator } from '../../components/payoutCalculator/PayoutCalculator';
import { PillStatus } from '../../components/pillsStatus/PillStatus';
import { ScorecardPizzaTracker } from '../../components/scorecardPizzaTracker/ScorecardPizzaTracker';
import {
    useGetAgentMetricDiscussionsQuery,
    useGetAgentNonMetricDiscussionsQuery,
} from '../../features/coaching/coachingSlice';
import { ToDoView } from '../../features/coaching/toDoView/ToDoView';
import { useGetCaManagerTlSearchQuery } from '../../features/correctiveActions/managerTlViewSearch/CaMangerTLView.slice';
import { MyCaDashboardScorecard } from '../../features/scorecardDashboard/myDashboardScorecard/MyCaDashboardScorecard';
import { CaStatusNames } from '../../utils/typeConstants';
import { useGetPrimaryApprovalWindowQuery } from '../compensation/compensationSlice';
import { selectCurrentEmpid, selectCurrentRole } from '../login/authSlice';
import './MetricDashboard.scss';
import {
    useGetDailyMetricsSummaryQuery,
    useGetEmpMonthlyMetricAttainmentQuery,
    // useGetDailyMetricsTableDetailsQuery,
    useGetEmpMonthlyResultReviewStatusQuery,
    useGetEmpPayoutLevelMetricRatesQuery,
    useGetEmployeeMetricAllPayoutsQuery,
    useGetEmployeeMetricPayoutStatusQuery,
    useGetMonthlyMetricHistoryQuery,
    useGetMonthlyMetricsSummaryQuery,
} from './metricDashboardSlice';
import AgentDiscussionsPlot from '../../components/agentDiscussionsPlot/AgentDiscussionsPlot';
import {
    getFilteredDataByMetricName,
    groupMetricDataByMonth,
    groupNonMetricDataByMonth,
} from '../../components/agentDiscussionsPlot/dataGrouping';
import { SUPERVISORROLE } from '../../constants';
import { GradientDefs } from '../../components/metricMonthlySummaryCard/GradientDefs';
import NonPayoutMetricsPanel from '../../components/nonPayoutMetricsPanel/NonPayoutMetricsPanel';
import StandardMetricsPanel from '../../components/standardMetricsPanel/StandardMetricsPanel';
import { MetricAttainmentPayoutCalculator } from '../../components/metricAttainmentPayoutCalculator/MetricAttainmentPayoutCalculator';
import { MetricCardsSkeletonLoader } from '../../components/metricCardsSkeletonLoader/MetricCardsSkeletonLoader';
import { SadFolderIllustration } from '../../components/emptyStateIllustrations/EmptyStateIllustrations';

export const MetricDashboard = () => {
    const loggedEmpNum = useSelector(selectCurrentEmpid);
    const loggedRole = useSelector(selectCurrentRole);
    /* Importing data from MyMetricsGreetingsFilter */
    const [metricStatusDataFilter, setMetricStatusDataFilter] = useState<any>([]);
    const [metricStatusIsFetching, setMetricStatusIsFetching] = useState<any>([]);
    const [agentDataValue, setAgentDataValue] = useState<any>([]);
    const [agentnumberValue, setAgentNumberValue] = useState<any>(0);
    const [scorecardVersion, setScorecardVersion] = useState(0);
    const [inputmonth, setMonth] = useState(new Date().getMonth() + 1);
    const [inputyear, setYear] = useState(new Date().getFullYear());
    const [selectedMetricName, setSelectedMetricName] = useState('');
    const [selectedMetricColorIndex, setSelectedMetricColorIndex] = useState(0);
    const [activeTab, setActiveTab] = useState<'standard' | 'additional'>('standard');
    const [empMonthlyResultReviewStatus, setEmpMonthlyResultReviewStatus] = useState<number>(0);
    const [myPayoutPillStatusDetails, setMyPayoutPillStatusDetails] = useState({
        status: '',
        icon: '',
        tooltip: '',
        bgColor: '',
    });
    const [agentNonMetricDiscussionsData, setAgentNonMetricDiscussionsData] = useState<any>();
    const [agentMetricDiscussionsData, setAgentMetricDiscussionsData] = useState<any>();

    /* API Queries */

    const { data: metricPayoutStatusData } = useGetEmployeeMetricPayoutStatusQuery(
        {
            employeenumber: agentnumberValue ? parseInt(agentnumberValue, 10) : 0,
            year: inputyear,
            month: inputmonth,
        },
        { skip: agentDataValue?.data?.length === 0 }
    );

    const { data: monthlyMetricsData, isFetching: isMonthlyMetricsFetching } = useGetMonthlyMetricsSummaryQuery(
        {
            employeenumber: agentnumberValue ? parseInt(agentnumberValue, 10) : 0,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: 1,
        },
        // {
        //     skip:
        //         !metricStatusDataFilter ||
        //         agentDataValue?.data?.length === 0 ||
        //         metricStatusDataFilter?.data?.length === 0 || !agentnumberValue,
        // }
    );
    const { data: monthlyMetricAttainmentData } = useGetEmpMonthlyMetricAttainmentQuery(
        {
            employeenumber: agentnumberValue ? agentnumberValue.toString() : 0,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
        },
        { skip: !agentnumberValue || !inputyear || !inputmonth || !scorecardVersion }
    );

    const { data: empPayoutLevelMetricRatesData } = useGetEmpPayoutLevelMetricRatesQuery(
        {
            employeenumber: agentnumberValue ? agentnumberValue.toString() : 0,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
        },
        { skip: !agentnumberValue || !inputyear || !inputmonth || !scorecardVersion }
    );

    // const { data: dailyMetricsTableData } = useGetDailyMetricsTableDetailsQuery(
    //     {
    //         employeenumber: parseInt(agentnumberValue, 10),
    //         year: inputyear,
    //         month: inputmonth,
    //         scorecardVersionId: scorecardVersion,
    //     },
    //     { skip: !agentnumberValue || !scorecardVersion }
    // );

    const { data: dailyMetricsData, isFetching: isDailyMetricsFetching } = useGetDailyMetricsSummaryQuery(
        {
            // employeenumber: parseInt(agentnumberValue, 10),
            employeenumber: agentnumberValue ? parseInt(agentnumberValue, 10) : 0,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: 1,
        },
        // {
        //     skip:
        //         !metricStatusDataFilter ||
        //         agentDataValue?.data?.length === 0 ||
        //         metricStatusDataFilter?.data?.length === 0 || !agentnumberValue,
        // }
    );
    const { data: monthlyMetricHistory } = useGetMonthlyMetricHistoryQuery(
        {
            employeenumber: parseInt(agentnumberValue, 10),
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
        },
        {
            skip:
                !metricStatusDataFilter ||
                agentDataValue?.data?.length === 0 ||
                metricStatusDataFilter?.data?.length === 0 ||
                !agentnumberValue,
        }
    );
    const { data: employeeMetricAllPayoutsData } = useGetEmployeeMetricAllPayoutsQuery(
        {
            employeenumber: parseInt(agentnumberValue, 10),
            scorecardroleid: 1,
        },
        {
            skip: !metricStatusDataFilter || agentDataValue?.data?.length === 0 || !agentnumberValue,
        }
    );
    const { data: EmpMonthlyResultReviewStatusData } = useGetEmpMonthlyResultReviewStatusQuery(
        {
            employeeNumber: parseInt(agentnumberValue, 10),
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
        },
        {
            skip: !agentnumberValue || !scorecardVersion || !inputyear || !inputmonth,
        }
    );
    const { data: primaryApprovalWindowData } = useGetPrimaryApprovalWindowQuery(
        {
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            approvalWindowConfigLevelId: 3,
        },
        { skip: !inputyear || !inputmonth || !scorecardVersion }
    );

    const { data: employeeCorrectiveActionsData } = useGetCaManagerTlSearchQuery(
        {
            employeeNumber: loggedEmpNum,
        },
        {
            skip: !loggedEmpNum,
        }
    );

    const { data: AgentNonMetricDiscussionsData } = useGetAgentNonMetricDiscussionsQuery(
        {
            employeeNumber: parseInt(agentnumberValue, 10),
            year: inputyear,
            month: inputmonth,
        },
        {
            skip: !agentnumberValue || !inputyear || !inputmonth,
        }
    );

    const { data: AgentMetricDiscussionsData } = useGetAgentMetricDiscussionsQuery(
        {
            employeeNumber: parseInt(agentnumberValue, 10),
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
        },
        {
            skip: !agentnumberValue || !inputyear || !inputmonth || !scorecardVersion,
        }
    );

    useEffect(() => {
        if (employeeMetricAllPayoutsData?.data?.length > 0) {
            const selectedMonthApprovalStatus: any = employeeMetricAllPayoutsData?.data?.filter(
                (item: any) =>
                    item.month === inputmonth &&
                    item.year === inputyear &&
                    item.scorecard_version_id === scorecardVersion
            );
            if (
                selectedMonthApprovalStatus?.length > 0 &&
                (selectedMonthApprovalStatus[0]?.approval_status_name === 'Disqualified' ||
                    selectedMonthApprovalStatus[0]?.approval_status_name === 'Disputed')
            ) {
                setMyPayoutPillStatusDetails({
                    status: selectedMonthApprovalStatus[0]?.approval_status_name,
                    icon: '',
                    tooltip: selectedMonthApprovalStatus[0]?.approval_status_name,
                    bgColor: '#E23636',
                });
            } else if (selectedMonthApprovalStatus?.length > 0) {
                setMyPayoutPillStatusDetails({
                    status: selectedMonthApprovalStatus[0]?.approval_status_name,
                    icon: '',
                    tooltip: selectedMonthApprovalStatus[0]?.approval_status_name,
                    bgColor: '#689F38',
                });
            } else setMyPayoutPillStatusDetails({ status: '', tooltip: '', icon: '', bgColor: '' });
        } else setMyPayoutPillStatusDetails({ status: '', tooltip: '', icon: '', bgColor: '' });
    }, [employeeMetricAllPayoutsData, primaryApprovalWindowData]);

    useEffect(() => {
        if (EmpMonthlyResultReviewStatusData?.data) {
            setEmpMonthlyResultReviewStatus(EmpMonthlyResultReviewStatusData?.data);
        }
    }, [EmpMonthlyResultReviewStatusData]);

    const statusData = metricStatusDataFilter?.data?.filter(
        (item: any) => item.scorecard_version_id === scorecardVersion
    );

    const payoutStatusData = metricPayoutStatusData?.data.filter(
        (item: any) => item.scorecard_version_id === scorecardVersion
    );

    useEffect(() => {
        if (monthlyMetricsData?.data?.length > 0) {
            const firstPayout = monthlyMetricsData.data.find((m: any) => m.is_payout_metric === true);
            setSelectedMetricName(firstPayout?.label ?? monthlyMetricsData.data[0]?.label);
            setSelectedMetricColorIndex(0);
        }
    }, [monthlyMetricsData]);

    const payoutMetrics = useMemo(
        () => monthlyMetricsData?.data?.filter((m: any) => m.is_payout_metric === true) ?? [],
        [monthlyMetricsData]
    );

    const nonPayoutMetrics = useMemo(
        () => monthlyMetricsData?.data?.filter((m: any) => m.is_payout_metric === false) ?? [],
        [monthlyMetricsData]
    );

    const handleCardClick = (metricName: string, index: number) => {
        setSelectedMetricName(metricName);
        setSelectedMetricColorIndex(index);
    };
    
    const isTierModel = useMemo(
        () => employeeMetricAllPayoutsData?.data?.some(
            (row: any) => row.scorecard_calculation_rule_id === 2
        ) ?? false,
        [employeeMetricAllPayoutsData]
    );

    const agentPayoutTableColumns = [
        { id: 1, name: 'Date', field: 'date', filter: true, template: 'dateBodyTemplate' },
        { id: 2, name: 'Scorecard', field: 'scorecard_name', filter: true, template: '' },
        {
            id: 3,
            name: isTierModel ? 'Meet Metrics' : 'Metrics Attained',
            field: 'total_metrics_attained',
            filter: true,
            template: 'metricsMetBodyTemplate',
        },
        ...(isTierModel ? [{
            id: 10,
            name: 'Exceeded Metrics',
            field: 'total_metrics_exceeded',
            filter: true,
            template: 'metricsExceededBodyTemplate',
        }] : []),
        { id: 4, name: 'Metrics Reported', field: 'total_metrics_reported', filter: true, template: '' },
        { id: 5, name: 'Eligible', field: 'eligible', filter: true, template: '' },
        { id: 6, name: 'Payout Amount', field: 'payout', filter: true, template: 'priceBodyTemplate' },
        { id: 7, name: 'Overall Status', field: 'approval_status_name', filter: true, template: '' },
        { id: 8, name: 'Agent Status', field: 'agent_status_name', filter: true, template: '' },
    ];

    const filterColumns = [
        'date',
        'scorecard_name',
        'total_metrics_attained',
        'total_metrics_reported',
        'eligible',
        'payout',
        'approval_status_name',
        'agent_status_name',
    ];

    const filteredCA =
        employeeCorrectiveActionsData?.data?.filter(
            (data: any) => data.ca_status_name === CaStatusNames.Release
        ) || [];

    useEffect(() => {
        if (AgentNonMetricDiscussionsData?.data) {
            setAgentNonMetricDiscussionsData(AgentNonMetricDiscussionsData?.data);
        } else setAgentNonMetricDiscussionsData([]);
    }, [AgentNonMetricDiscussionsData]);

    useEffect(() => {
        if (AgentMetricDiscussionsData?.data?.length > 0) {
            const filteredData = getFilteredDataByMetricName(
                AgentMetricDiscussionsData?.data,
                selectedMetricName
            );
            setAgentMetricDiscussionsData(filteredData);
        } else setAgentMetricDiscussionsData([]);
    }, [AgentMetricDiscussionsData, selectedMetricName]);

    const filteredAgentNonMetricDiscussionsData = agentNonMetricDiscussionsData
        ? groupNonMetricDataByMonth(agentNonMetricDiscussionsData)
        : [];
    const filteredAgentMetricDiscussionsData = agentMetricDiscussionsData
        ? groupMetricDataByMonth(agentMetricDiscussionsData)
        : [];
    
    const isPercentageRule =
        monthlyMetricAttainmentData?.data?.length > 0 &&
        monthlyMetricAttainmentData?.data[0]?.scorecard_calculation_rule_id === 2;

    const renderMetricAttainmentSection = () => {
        if (isPercentageRule) {
            return <div />;
        }

        if (monthlyMetricsData?.data?.length > 0 && payoutStatusData && payoutStatusData[0]) {
            return (
                <div className="col">
                    <MetricProgress data={payoutMetrics} selectedmetrics={payoutStatusData[0]} />
                </div>
            );
        }

        return <div />;
    };

    // Loading vs. genuinely-empty gate. Previously both states rendered the same "No results
    // found" message, which was misleading during a normal fetch - now an in-flight fetch shows
    // the rich skeleton loader instead, and "No results found" is reserved for a real empty result.
    const isMetricsLoading = isMonthlyMetricsFetching || isDailyMetricsFetching;
    const hasMetricsData = dailyMetricsData?.data.length > 0;

    // Precomputed (not a nested ternary in JSX) - matches this file's if/else style elsewhere.
    let metricsSectionContent: React.ReactNode;
    if (isMetricsLoading) {
        metricsSectionContent = (
            <div className="card shadow row ms-3 me-3 mt-4">
                <div className="p-3">
                    <MetricCardsSkeletonLoader />
                </div>
            </div>
        );
    } else if (hasMetricsData) {
        metricsSectionContent = (
            <>
                <GradientDefs />
                <div className="card shadow row ms-3 me-3 mt-4">
                    <div className="p-3">
                        {nonPayoutMetrics.length > 0 && (
                        <div className="metrics-tab-switcher">
                            <button
                                className={`metrics-tab ${activeTab === 'standard' ? 'metrics-tab--active' : ''}`}
                                onClick={() => setActiveTab('standard')}
                                type="button"
                            >
                                <img src={standardMetricsIcon} alt="Standard Metrics" style={{ width: '16px', height: '16px' }} />
                                Standard Metrics
                            </button>
                            <button
                                className={`metrics-tab ${activeTab === 'additional' ? 'metrics-tab--active' : ''} ${nonPayoutMetrics.length === 0 ? 'metrics-tab--disabled' : ''}`}
                                onClick={() => setActiveTab('additional')}
                                disabled={nonPayoutMetrics.length === 0}
                                type="button"
                            >
                                <img src={additionalMetricsIcon} alt="Additional Metrics" style={{ width: '16px', height: '16px' }} />
                                Additional Metrics (Non Payout)
                            </button>
                        </div>
                        )}
                        <div className="metrics-tab-content">
                            {activeTab === 'standard' && (
                                <StandardMetricsPanel
                                    metrics={payoutMetrics}
                                    dailyMetricsData={dailyMetricsData}
                                    monthlyMetricHistory={monthlyMetricHistory}
                                    scorecardVersionId={scorecardVersion ?? 0}
                                    employeeNumber={loggedEmpNum?.toString() ?? ''}
                                    fromMetricDashboard
                                    selectedMetricName={selectedMetricName}
                                    selectedMetricColorIndex={selectedMetricColorIndex}
                                    onCardClick={handleCardClick}
                                >
                                    {loggedRole === SUPERVISORROLE && (
                                        <AgentDiscussionsPlot
                                            nonMetricDiscussionsData={filteredAgentNonMetricDiscussionsData}
                                            metricDiscussionsData={filteredAgentMetricDiscussionsData}
                                            recieverId={agentnumberValue}
                                            receiverName={metricStatusDataFilter?.data[0]?.first_name}
                                            inputYear={inputyear}
                                            inputMonth={inputmonth}
                                            metricIndex={selectedMetricColorIndex}
                                            metricOmitted={
                                                monthlyMetricsData?.data?.find((m: any) => m.label === selectedMetricName)?.metric_omitted || false
                                            }
                                        />
                                    )}
                                </StandardMetricsPanel>
                            )}
                            {activeTab === 'additional' && (
                                <NonPayoutMetricsPanel
                                    metrics={nonPayoutMetrics}
                                    dailyMetricsData={dailyMetricsData}
                                    monthlyMetricHistory={monthlyMetricHistory}
                                    scorecardVersionId={scorecardVersion}
                                    employeeNumber={loggedEmpNum?.toString() ?? ''}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {hasMetricsData ? (
                    <div className="col-12 text-end exportexcel float-end mt-2 mb-2">
                        <ExportDailyMetric
                            inputmonth={inputmonth}
                            inputyear={inputyear}
                            dailyMetricsDataLength={dailyMetricsData?.data?.length}
                            agentDataValueLength={agentDataValue?.data?.length}
                            metricStatusDataFilterLength={metricStatusDataFilter?.data?.length}
                            agentnumberValue={agentnumberValue}
                            scorecardVersion={scorecardVersion}
                            FullEmployeeName={
                                metricStatusDataFilter?.data?.length > 0
                                    ? `${metricStatusDataFilter?.data[0].first_name} ${metricStatusDataFilter?.data[0].last_name}`
                                    : ''
                            }
                        />
                    </div>
                ) : (
                    ''
                )}
            </>
        );
    } else {
        metricsSectionContent = (
            <div className="card mt-4 ms-3 shadow bg-body rounded border-0">
                <div className="metric-dashboard-empty">
                    <div className="metric-dashboard-empty__illustration">
                        <SadFolderIllustration />
                    </div>
                    <div className="metric-dashboard-empty__divider" />
                    <p className="metric-dashboard-empty__title">No Results found for the Performance Period.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <MyMetricGreetingFilter
                metricStatusDataFilter={setMetricStatusDataFilter}
                metricStatusIsFetchingValue={setMetricStatusIsFetching}
                agentData={setAgentDataValue}
                agentnumber={agentnumberValue}
                setAgentNumber={setAgentNumberValue}
                scorecardVersionValue={setScorecardVersion}
                inputmonth={inputmonth}
                setInputMonth={setMonth}
                inputyear={inputyear}
                setInputYear={setYear}
            />

            {metricStatusIsFetching === false && metricStatusDataFilter?.data.length > 0 && (
                <MyMetricGreeting
                    employeeName={metricStatusDataFilter?.data[0]?.first_name}
                    totalMetricsAttained={metricStatusDataFilter?.data[0]?.total_metrics_attained}
                    totalMetricsReported={metricStatusDataFilter?.data[0]?.total_metrics_reported}
                    totalMetricsTargeted={metricStatusDataFilter?.data[0]?.total_metrics_targeted}
                    scorecardCalculationRuleId={monthlyMetricsData?.data[0]?.scorecard_calculation_rule_id}
                />
            )}

            {metricsSectionContent}
            <div style={{ display: statusData?.length > 0 ? '' : 'none' }} />
            {isPercentageRule && payoutStatusData && payoutStatusData[0] && (
                <div className="row ms-3 me-3">
                    <div className="col-12">
                        <MetricAttainmentPayoutCalculator
                            data={monthlyMetricAttainmentData?.data}
                            payoutRates={empPayoutLevelMetricRatesData?.data}
                            resultData={payoutStatusData[0]}
                        />
                    </div>
                </div>
            )}
            <div className="row row-cols-1 row-cols-md-2 g-2 ms-3 me-3">
                {renderMetricAttainmentSection()}
                {payoutStatusData && payoutStatusData[0] && payoutStatusData[0].scorecard_calculation_rule_id === 1 && (
                    <div className="col">
                        <PayoutCalculator empMetricData={payoutStatusData[0]} />
                    </div>
                )}
            </div>
            <br />

            <Panel
                id="AllMetrics"
                header={
                    <div>
                        <span style={{ paddingRight: '10px' }}>All Metrics</span>
                        {empMonthlyResultReviewStatus === 0 && (
                            <Tag severity="warning" value="Needs Review" />
                        )}
                        {empMonthlyResultReviewStatus === 1 && (
                            <Tag severity="warning" value="Needs Review" />
                        )}
                        {empMonthlyResultReviewStatus === 2 && <Tag severity="success" value="Reviewed" />}
                        {empMonthlyResultReviewStatus === 3 && <Tag severity="danger" value="Disputed" />}
                    </div>
                }
                toggleable
                collapsed
                className="card mt-5 ms-4 me-4 shadow bg-body rounded border-0"
            >
                <div className="row mt-3 me-3">
                    {/* {dailyMetricsTableData?.data.length > 0 ? ( */}
                        <MyDailyMetricsTable
                            // data={dailyMetricsTableData}
                            /* columns={agentMetricsColumns} */
                            employeenumber={agentnumberValue}
                            year={inputyear}
                            month={inputmonth}
                            scorecardVersionId={scorecardVersion}
                            monthlyMetricsData={monthlyMetricsData}
                        />
                    {/* ) : (
                        <div>No Results Found</div>
                    )} */}
                </div>
            </Panel>
            <Panel
                headerTemplate={(options) =>
                    PillStatus({ options, Headertext: 'Payouts', pillDetails: myPayoutPillStatusDetails })
                }
                toggleable
                collapsed
                className="approvalPanel card mt-5 ms-4 me-4 shadow bg-body rounded border-0"
            >
                <ScorecardPizzaTracker
                    inputYear={inputyear}
                    inputMonth={inputmonth}
                    inputScorecardVersion={scorecardVersion}
                />
                <div className="payoutTitle mt-2"> All Payouts</div>
                {employeeMetricAllPayoutsData?.data.length > 0 ? (
                    <MetricPayoutTable
                        data={employeeMetricAllPayoutsData?.data}
                        scorecardroleid={1}
                        columns={agentPayoutTableColumns}
                        uniqueKey="key"
                        filterColumns={filterColumns}
                        approvalGrid="AssociatePayout"
                        currentApprovalWindow={primaryApprovalWindowData?.data[0]?.approval_window_status_id}
                        year={inputyear}
                        month={inputmonth}
                        scorecardVersion={scorecardVersion}
                        reviewStatusId={empMonthlyResultReviewStatus}
                    />
                ) : (
                    <div className="card mt-3 ms-1 me-1 shadow bg-body rounded border-0">
                        <div className="card-body p-0 pe-5 ps-4 align-middle text-center">
                            <div className="row" style={{ height: '15vh' }}>
                                <div className="col-md-12" style={{ margin: 'auto' }}>
                                    <p className="align-middle">No results found for Payouts.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Panel>
            {
                <Panel
                    header="My To do’s"
                    toggleable
                    collapsed
                    className="card mt-5 ms-4 me-4 shadow bg-body rounded border-0"
                >
                    <ToDoView />
                </Panel>
            }
            {
                <Panel
                    id="AllMetrics"
                    header={
                        <div>
                            <span style={{ paddingRight: '10px' }}>My Corrective Actions</span>

                            {filteredCA?.length > 0 && <Tag severity="warning" value="Needs Review" />}
                        </div>
                    }
                    toggleable
                    collapsed
                    className="approvalPanel card mt-5 ms-4 me-4 shadow bg-body rounded border-0"
                >
                    <MyCaDashboardScorecard view />
                </Panel>
            }
        </>
    );
};